import { doc, getDoc, runTransaction } from "firebase/firestore";
import { fs, functions } from "../firebase.ts";
import { httpsCallable } from "firebase/functions";
import { startAuthentication } from "@simplewebauthn/browser";

// heartkey
type Status = {
    text: string;
    color: "green" | "red" | "orange";
    content?: string | null;
    signature?: string | null;
    kid?: string | null;
    iv?: string | null;
    encryptedSymmetricKey?: string | null;
    encryptedContent?: string | null;
    decryptedContent?: string | null;
    data?: any;
};

export class Crypto {
    private algorithm = {
        name: "RSA-OAEP",
        hash: "SHA-256"
    };

    constructor() {}

    // trustkey
    async update(uid: string, tid: string): Promise<Status> {
        try {
            const userDocRef = doc(fs, "users", uid);
            const newKeyDocRef = doc(fs, "trustkeys", tid);

            await runTransaction(fs, async (transaction) => {
                const userDoc = await transaction.get(userDocRef);
                if (!userDoc.exists()) {
                    throw new Error(`User document ${uid} does not exist`);
                }

                const { trustkey } = userDoc.data() as { trustkey?: string };
                if (trustkey) {
                    const oldKeyDocRef = doc(fs, "trustkeys", trustkey);
                    transaction.delete(oldKeyDocRef);
                }

                transaction.update(userDocRef, { trustkey: tid });
                transaction.set(newKeyDocRef, { uid });
            });

            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            return {
                text:
                    "An error occurred while updating your trustkey: " +
                    error.message,
                color: "red"
            };
        }
    }

    // trustkey: not used
    async update2(tid: string): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "trustkey/update" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const { status }: any = (
                await verify({ assertion, action: "trustkey/update", tid })
            ).data;

            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async add(name: string): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "heartkey/add" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const { kid }: any = (
                await verify({ assertion, action: "heartkey/add", name })
            ).data;

            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.")
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    else
                        return {
                            text:
                                "Passkey registration was cancelled: " +
                                error.message,
                            color: "orange"
                        };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async set(kid: string): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "heartkey/set" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const { status }: any = (
                await verify({ assertion, action: "heartkey/set", kid })
            ).data;

            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // redundant ??
    // async get(kid: string): Promise<Status> {
    //     try {
    //         const getOpts = httpsCallable(functions, "getActionOptions");
    //         const options: any = (await getOpts({ action: "heartkey/get" }))
    //             .data;
    //         const assertion = await startAuthentication({
    //             optionsJSON: options
    //         });

    //         const verify = httpsCallable(functions, "verifyAction");
    //         const {
    //             iv,
    //             encryptedSymmetricKey,
    //             encryptedContent,
    //             signature,
    //             message
    //         }: any = (await verify({ assertion, action: "heartkey/get", kid }))
    //             .data;

    //         return {
    //             text: message,
    //             color: "green",
    //             iv,
    //             encryptedSymmetricKey,
    //             encryptedContent, //encryptedPrivateKey
    //             signature
    //         };
    //     } catch (error: any) {
    //         switch (error.name) {
    //             case "NotAllowedError":
    //                 return {
    //                     text: "Passkey verification was cancelled.",
    //                     color: "orange"
    //                 };
    //             default:
    //                 return { text: error.message, color: "red" };
    //         }
    //     }
    // }

    async mint(content: Object): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "heartkey/mint" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const data: any = (
                await verify({ assertion, action: "heartkey/mint", content })
            ).data;

            return {
                text: "Success",
                color: "green",
                data
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async encrypt(
        content: string
        //kid: string | null = null TODO:
    ): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (
                await getOpts({
                    action: "heartkey/encrypt",
                    kid: null,
                    content
                })
            ).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const {
                encryptedSymmetricKey,
                kid, // TODO: remove
                iv,
                encryptedContent,
                signature
            }: any = (
                await verify({
                    assertion,
                    action: "heartkey/encrypt",
                    kid: null,
                    content
                })
            ).data;

            return {
                text: "Success",
                color: "green",
                kid,
                encryptedSymmetricKey,
                iv,
                encryptedContent,
                signature
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async decrypt(
        kid: string,
        iv: string,
        encryptedSymmetricKey: string,
        encryptedContent: string
    ): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (
                await getOpts({
                    action: "heartkey/decrypt",
                    kid,
                    iv,
                    encryptedSymmetricKey,
                    encryptedContent
                })
            ).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const decryptedContent: any = (
                await verify({
                    assertion,
                    action: "heartkey/decrypt",
                    kid,
                    iv,
                    encryptedSymmetricKey,
                    encryptedContent
                })
            ).data;

            return {
                text: "Success",
                color: "green",
                decryptedContent
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // get symmetric key to decrypt on the client
    async get(
        isSafe: boolean, // safe or hb
        kid: string,
        symmetricKey: string // encrypted
    ): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (
                await getOpts({
                    action: "heartkey/get",
                    isSafe,
                    kid,
                    symmetricKey
                })
            ).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const decryptedContent: any = (
                await verify({
                    assertion,
                    action: "heartkey/get",
                    isSafe,
                    kid,
                    symmetricKey
                })
            ).data;

            return {
                text: "Success",
                color: "green",
                decryptedContent // symmetric key
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async sign(content: string, kid: string | null = null): Promise<Status> {
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (
                await getOpts({ action: "heartkey/sign", kid, content })
            ).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const signature: any = (
                await verify({
                    assertion,
                    action: "heartkey/sign",
                    kid,
                    content
                })
            ).data;

            return {
                text: "Success",
                color: "green",
                content,
                signature
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    return {
                        text: "Passkey verification was cancelled.",
                        color: "orange"
                    };
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // via client (using kid)
    async verify(
        kid: string,
        data: string,
        signature: string
    ): Promise<boolean> {
        // 2. Import the PEM public key for use with the Web Crypto API
        const publicKeyBuffer = this.base64urlToArrayBuffer(kid);
        const cryptoKey = await window.crypto.subtle.importKey(
            "spki", // 'SubjectPublicKeyInfo' - the standard format for public keys
            publicKeyBuffer,
            this.algorithm,
            true, // The key is extractable (though we don't need it to be)
            ["verify"] // We intend to use this key for verification
        );

        // 3. Prepare the message and signature for verification
        const encoder = new TextEncoder();
        const messageBuffer = encoder.encode(data);

        // The signature is base64, so we need to decode it into a buffer
        const signatureBuffer = Uint8Array.from(atob(signature), (c) =>
            c.charCodeAt(0)
        );

        // 4. Perform the verification
        return await window.crypto.subtle.verify(
            this.algorithm,
            cryptoKey,
            signatureBuffer,
            messageBuffer
        );
    }

    /**
     * Decodes a base64url string to an ArrayBuffer.
     */
    private base64urlToArrayBuffer(base64url: string): ArrayBuffer {
        const base64 = base64url
            .replace(/-/g, "+")
            .replace(/_/g, "/")
            .padEnd(Math.ceil(base64url.length / 4) * 4, "=");

        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }

    // deprecated

    // via client (using publicKeyPem)
    async verify2(
        uid: string,
        kid: string,
        data: string,
        signature: string
    ): Promise<boolean> {
        // 1. Fetch the signer's public key document from Firestore
        const keyDocRef = doc(fs, "users", uid, "heartkeys", kid);
        const keyDocSnap = await getDoc(keyDocRef);

        if (!keyDocSnap.exists()) {
            console.error("Public key not found for the specified signer.");
            return false;
        }
        const { publicKey: publicKeyPem } = keyDocSnap.data();

        // 2. Import the PEM public key for use with the Web Crypto API
        const publicKeyBuffer = this.pemToBuffer(publicKeyPem);
        const cryptoKey = await window.crypto.subtle.importKey(
            "spki", // 'SubjectPublicKeyInfo' - the standard format for public keys
            publicKeyBuffer,
            this.algorithm,
            true, // The key is extractable (though we don't need it to be)
            ["verify"] // We intend to use this key for verification
        );

        // 3. Prepare the message and signature for verification
        const encoder = new TextEncoder();
        const messageBuffer = encoder.encode(data);

        // The signature is base64, so we need to decode it into a buffer
        const signatureBinStr = window.atob(signature);
        const signatureBuffer = new Uint8Array(signatureBinStr.length);
        for (let i = 0; i < signatureBinStr.length; i++) {
            signatureBuffer[i] = signatureBinStr.charCodeAt(i);
        }

        // 4. Perform the verification
        return await window.crypto.subtle.verify(
            this.algorithm,
            cryptoKey,
            signatureBuffer,
            messageBuffer
        );
    }

    /**
     * A helper function to convert a PEM-encoded key to an ArrayBuffer.
     * The Web Crypto API requires key data in a raw buffer format.
     * @param {string} pem The PEM-encoded key string.
     * @returns {ArrayBuffer}
     */
    private pemToBuffer(pem: string): ArrayBuffer {
        // 1. Remove the header and footer lines
        const b64 = pem
            .replace(/-----BEGIN PUBLIC KEY-----/, "")
            .replace(/-----END PUBLIC KEY-----/, "")
            .replace(/\s/g, ""); // Remove all whitespace

        // 2. Decode the Base64 string into a binary string
        const binaryStr = window.atob(b64);

        // 3. Convert the binary string to an ArrayBuffer
        const len = binaryStr.length;
        const buffer = new ArrayBuffer(len);
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
        }
        return buffer;
    }
}
